// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson <hi@ben.gy>
// Additional terms apply under AGPL-3.0 section 7(b) — see ADDITIONAL-TERMS.md

import type { FromCounter, FromReader } from './core/protocol';
import { merge } from './core/totals';
import type { ModelEntry } from './data/models';
import type { OverLimitRow, Totals } from './types';

/**
 * How many counter workers.
 *
 * THE POOL SIZE IS A MEMORY DECISION, NOT A CORE-COUNT ONE, and that is the
 * opposite of the obvious implementation. The chunk buffers are trivial — lanes
 * x 2 x 4 MB — but each worker isolate holds its own copy of the tokenizer's
 * rank tables, measured at roughly 20 MB of JS heap and 65 MB of RSS for o200k,
 * plus its own merge cache. Eight lanes of that is half a gigabyte of resident
 * memory before a single row is read, which is how an iOS tab gets killed
 * halfway through a file.
 *
 * So `deviceMemory` bounds it where the browser reports one (Chromium), and the
 * floor everywhere else is conservative.
 */
export function poolSize(): number {
  if (typeof navigator === 'undefined') return 4;
  const cores = navigator.hardwareConcurrency ?? 4;
  const gb = (navigator as Navigator & { deviceMemory?: number }).deviceMemory;
  // ~150 MB per lane once ranks, merge cache and chunk buffers are counted, and
  // never more than half the reported memory.
  const byMemory = gb === undefined ? 8 : Math.max(1, Math.floor((gb * 1024) / 2 / 150));
  return Math.max(1, Math.min(8, cores - 1, byMemory));
}

/**
 * Chunk size. Big enough that per-message overhead is noise, small enough that
 * lanes x 2 x this stays modest.
 */
export function chunkBytes(fileSize: number): number {
  if (fileSize < 8 * 1024 * 1024) return 1 * 1024 * 1024;
  return 4 * 1024 * 1024;
}

export interface RunHandle {
  readonly result: Promise<RunResult>;
  cancel(): void;
}

export interface RunResult {
  readonly totals: Totals;
  readonly overLimit: readonly OverLimitRow[];
  readonly keptCoversAbove: number;
  readonly truncatedByCap: number;
  readonly decompressedBytes: number;
  readonly elapsedMs: number;
}

export interface RunCallbacks {
  onProgress(p: { bytesRead: number; bytesTotal: number; decompressedRead: number; rows: number }): void;
  onReady(describe: string): void;
  onEvent(message: string): void;
}

/**
 * Stream `file` through a pool of counters.
 *
 * The shape is: one reader worker owns the file and cuts it at line boundaries;
 * N counter workers each hold their own tokenizer and fold rows into their own
 * totals; the reader hands out chunks only against credits, so nothing queues.
 * At the end every counter's totals are merged.
 */
export function run(file: File, model: ModelEntry, limit: number | null, cb: RunCallbacks): RunHandle {
  const started = performance.now();
  const lanes = poolSize();

  const reader = new Worker(new URL('./workers/reader.worker.ts', import.meta.url), { type: 'module' });
  const counters = Array.from(
    { length: lanes },
    () => new Worker(new URL('./workers/counter.worker.ts', import.meta.url), { type: 'module' }),
  );

  let settled = false;
  let cancelled = false;
  const partials: { totals: Totals; overLimit: readonly OverLimitRow[]; keptCoversAbove: number; truncatedByCap: number }[] = [];
  let rows = 0;
  let readerDone = false;
  let decompressedBytes = 0;
  let readyCount = 0;
  let lastBytesRead = 0;
  let lastTotal = 0;
  let lastDecompressed = 0;

  const teardown = (): void => {
    reader.terminate();
    for (const c of counters) c.terminate();
  };

  const result = new Promise<RunResult>((resolve, reject) => {
    const fail = (message: string): void => {
      if (settled) return;
      settled = true;
      teardown();
      reject(new Error(message));
    };

    const maybeFinish = (): void => {
      if (settled || !readerDone || partials.length < counters.length) return;
      settled = true;
      teardown();

      const totals = partials[0].totals;
      for (let i = 1; i < partials.length; i++) merge(totals, partials[i].totals);
      if (totals.rows === 0) totals.shortest = 0;

      const overLimit = partials.flatMap((p) => [...p.overLimit]).sort((a, b) => b.tokens - a.tokens);
      // The list each lane kept covers everything above ITS smallest kept row;
      // together they only jointly cover above the LARGEST of those floors.
      const keptCoversAbove = partials.reduce((max, p) => Math.max(max, p.keptCoversAbove), 0);
      const truncatedByCap = partials.reduce((sum, p) => sum + p.truncatedByCap, 0);

      resolve({
        totals,
        overLimit,
        keptCoversAbove,
        truncatedByCap,
        decompressedBytes,
        elapsedMs: performance.now() - started,
      });
    };

    reader.onmessage = (ev: MessageEvent): void => {
      const msg = ev.data as FromReader;
      if (msg.kind === 'error') return fail(msg.message);
      if (msg.kind === 'progress') {
        lastBytesRead = msg.bytesRead;
        lastTotal = msg.bytesTotal;
        lastDecompressed = msg.decompressedRead;
        cb.onProgress({ bytesRead: msg.bytesRead, bytesTotal: msg.bytesTotal, decompressedRead: msg.decompressedRead, rows });
        return;
      }
      if (msg.kind === 'reader-done') {
        readerDone = true;
        decompressedBytes = msg.decompressedBytes;
        maybeFinish();
      }
    };
    reader.onerror = (e) => fail(e.message || 'The reader worker failed.');

    for (const counter of counters) {
      counter.onmessage = (ev: MessageEvent): void => {
        const msg = ev.data as FromCounter;
        if (msg.kind === 'error') return fail(msg.message);
        if (msg.kind === 'ready') {
          readyCount += 1;
          if (readyCount === 1) cb.onReady(msg.describe);
          if (readyCount === counters.length && !cancelled) {
            // Only start reading once EVERY counter holds a tokenizer. Starting
            // earlier means the first chunks queue against workers that are still
            // downloading a 17 MB tokenizer.json.
            reader.postMessage({ kind: 'start', file, chunkBytes: chunkBytes(file.size), ports }, ports);
          }
          return;
        }
        if (msg.kind === 'counted') {
          rows += msg.rows;
          cb.onProgress({ bytesRead: lastBytesRead, bytesTotal: lastTotal, decompressedRead: lastDecompressed, rows });
          return;
        }
        if (msg.kind === 'counter-done') {
          partials.push({
            totals: msg.totals,
            overLimit: msg.overLimit,
            keptCoversAbove: msg.keptCoversAbove,
            truncatedByCap: msg.truncatedByCap,
          });
          maybeFinish();
        }
      };
      counter.onerror = (e) => fail(e.message || 'A counter worker failed.');
    }

    const channels = counters.map(() => new MessageChannel());
    const ports = channels.map((c) => c.port1);
    counters.forEach((counter, i) => {
      counter.postMessage({ kind: 'init', port: channels[i].port2, modelId: model.id, limit }, [channels[i].port2]);
    });
  });

  return {
    result,
    cancel(): void {
      cancelled = true;
      reader.postMessage({ kind: 'cancel' });
      teardown();
    },
  };
}

/** Bytes per second, for the ETA. Returns null until there is enough to be meaningful. */
export function throughput(decompressedRead: number, elapsedMs: number): number | null {
  if (elapsedMs < 400 || decompressedRead < 512 * 1024) return null;
  return decompressedRead / (elapsedMs / 1000);
}

export function formatEta(remainingBytes: number, bytesPerSecond: number | null): string {
  if (bytesPerSecond === null || bytesPerSecond <= 0) return 'measuring…';
  const seconds = remainingBytes / bytesPerSecond;
  if (seconds < 90) return `about ${Math.max(1, Math.round(seconds))}s left`;
  if (seconds < 5400) return `about ${Math.round(seconds / 60)} min left`;
  return `about ${(seconds / 3600).toFixed(1)} hours left`;
}
